// src/pages/PreSchool/api.js
import { getNetwork } from '../../utils/getNetwork';
import { API_ROOT } from '../../config';
import { META, MOCK_ROWS } from './mockData';
import { occState } from './occupancy';

export const USE_MOCK = false;

const PRESCHOOL_ROOT = 'https://sc.smartkulsary.kz/api/kulsary/api';

const META_ENDPOINT = '/edu/arm/meta';
const LIST_ENDPOINT = '/edu/arm/preschools';

export class ApiError extends Error {
  constructor(status, body) {
    super('api_error');
    this.status = status;
    this.body = body;
  }
}

const FIELD_ALIASES = {
  name_kz: 'nameKz',
  address: 'addr',
  address_kz: 'addrKz',
  locality_type: 'locType',
  latitude: 'lat',
  longitude: 'lon',
  language: 'lang',
  kontingent: 'kont',
  available_seats: 'free',
  napravleniya: 'napr',
  zachisleno: 'zach',
  report_period: 'period',
  is_active: 'active',
};

function fromApi(item) {
  if (!item || typeof item !== 'object') return item;
  const out = { ...item };
  Object.entries(FIELD_ALIASES).forEach(([apiKey, frontKey]) => {
    if (apiKey in item) out[frontKey] = item[apiKey];
  });
  return out;
}

function toApi(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const out = { ...payload };
  Object.entries(FIELD_ALIASES).forEach(([apiKey, frontKey]) => {
    if (frontKey in payload) {
      out[apiKey] = payload[frontKey];
      delete out[frontKey];
    }
  });
  return out;
}

function normalizeMeta(meta) {
  if (!meta || typeof meta !== 'object') return meta;
  const src = meta.dictionaries || {};
  const dictionaries = {};
  Object.keys(src).forEach((group) => {
    const raw = src[group];
    const list = Array.isArray(raw)
      ? raw
      : raw && typeof raw === 'object'
      ? Object.keys(raw).map((k) =>
          raw[k] && typeof raw[k] === 'object' ? { v: k, ...raw[k] } : { v: k, ru: raw[k] }
        )
      : [];
    dictionaries[group] = list.map((o) => {
      // Справочник может прийти массивом примитивов (например, периоды ["2026", ...]).
      if (o === null || typeof o !== 'object') {
        const s = o == null ? '' : String(o);
        return { v: o, ru: s, kk: s };
      }
      const v =
        o.v != null
          ? o.v
          : o.value != null
          ? o.value
          : o.code != null
          ? o.code
          : o.id != null
          ? o.id
          : o.key != null
          ? o.key
          : o.year != null
          ? o.year
          : o.period;
      const ru =
        o.ru != null
          ? o.ru
          : o.name_ru != null
          ? o.name_ru
          : o.label_ru != null
          ? o.label_ru
          : o.name != null
          ? o.name
          : o.label != null
          ? o.label
          : v != null
          ? String(v)
          : '';
      const kk =
        o.kk != null
          ? o.kk
          : o.kz != null
          ? o.kz
          : o.name_kk != null
          ? o.name_kk
          : o.name_kz != null
          ? o.name_kz
          : o.label_kk != null
          ? o.label_kk
          : ru;
      return { ...o, v, ru, kk };
    });
  });

  // Синонимы имён групп: как их может назвать бэк ↔ как их читают columns.jsx / Preschools.jsx.
  // Если группа пришла под «бэковым» именем, а «фронтового» ключа нет — дублируем ссылку.
  const GROUP_ALIASES = {
    language: 'lang',
    languages: 'lang',
    types: 'type',
    forms: 'form',
    localities: 'locality',
    districts: 'district',
    report_period: 'period',
    report_periods: 'period',
    periods: 'period',
    is_active: 'active',
    statuses: 'active',
  };
  Object.keys(GROUP_ALIASES).forEach((from) => {
    const to = GROUP_ALIASES[from];
    if (dictionaries[from] && !dictionaries[to]) dictionaries[to] = dictionaries[from];
  });

  // /meta может не отдать справочники period/active — без них селекты формы
  // «Отчётный период» и «Статус активности» пустые. Подставляем локальные.
  ['period', 'active'].forEach((group) => {
    if (!dictionaries[group] || !dictionaries[group].length) {
      dictionaries[group] = META.dictionaries[group];
    }
  });

  return { ...meta, dictionaries };
}

export async function fetchPreschoolMeta() {
  if (USE_MOCK) return wait(200).then(() => META);
  const res = await getNetwork().get(PRESCHOOL_ROOT + META_ENDPOINT);
  return normalizeMeta(res.data);
}

export async function fetchPreschools(params = {}) {
  const { page = 1, per = 15, q = '', locality = '', type = '', fill_state = '' } = params;
  if (USE_MOCK)
    return wait(300).then(() => queryMock(mockStore, { page, per, q, locality, type, fill_state }));
  const query = clean({ page, per, q, locality, type, fill_state });
  const res = await getNetwork().get(PRESCHOOL_ROOT + LIST_ENDPOINT, { params: query });
  const data = (res && res.data) || {};
  const items = Array.isArray(data.items) ? data.items : Array.isArray(data.rows) ? data.rows : [];
  return {
    rows: items.map(fromApi),
    total: typeof data.total === 'number' ? data.total : items.length,
  };
}

export async function createPreschool(payload) {
  if (USE_MOCK) return mockCreate(payload);
  try {
    const res = await getNetwork().post(PRESCHOOL_ROOT + LIST_ENDPOINT, toApi(payload));
    if (res && (res.status === 201 || res.status === 200)) {
      const body = res.data || {};
      const mapped = fromApi(body.record || body);
      if (body.fill_state != null && mapped.fill_state == null) mapped.fill_state = body.fill_state;
      if (body.deviation != null && mapped.deviation == null) mapped.deviation = body.deviation;
      if (Array.isArray(body.warnings)) mapped.warnings = body.warnings;
      return mapped;
    }
    if (res) throw new ApiError(res.status, res.data);
    throw new ApiError(0, null);
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError((e.response && e.response.status) || 0, e.response && e.response.data);
  }
}

let mockStore = MOCK_ROWS.slice();
let seq = Math.max(...MOCK_ROWS.map((r) => r.id)) + 1;

function mockCreate(payload) {
  return wait(350).then(() => {
    const created = {
      id: seq++,
      locality: payload.locality || 'г.Кульсары',
      locType: payload.locType || 'городская местность',
      ...payload,
    };
    mockStore = [created, ...mockStore];
    return created;
  });
}

function queryMock(store, { page, per, q, locality, type, fill_state }) {
  const normMin = META.thresholds.occ_norm_min;
  const needle = String(q || '')
    .trim()
    .toLowerCase();
  const filtered = store.filter((r) => {
    if (locality && r.locality !== locality) return false;
    if (type && r.type !== type) return false;
    if (fill_state && occState(r.seats, r.kont, normMin) !== fill_state) return false;
    if (needle) {
      const hay = (
        r.name +
        ' ' +
        (r.nameKz || '') +
        ' ' +
        r.addr +
        ' ' +
        (r.addrKz || '')
      ).toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / per));
  const safePage = Math.min(Math.max(1, page), pages);
  const rows = filtered
    .slice((safePage - 1) * per, safePage * per)
    .map((r) => ({ ...r, fill_state: occState(r.seats, r.kont, normMin) }));
  return { rows, total };
}

function clean(obj) {
  const out = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v !== '' && v != null) out[k] = v;
  });
  return out;
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
